import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Breadcrumbs,
  Typography,
  Button,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Table,
  IconButton,
  Stack
} from '@mui/material';
import { Add, Home as HomeIcon } from '@mui/icons-material';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { EditIcon, ProductDocumentsIcon, TrashIcon } from '../../../common/Icons';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { SkeletonTableRow } from '../../../common/SkeletonTableRow';
import { listaEmpresas } from '../companies/configs/functions';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import {
  atualizaDepartamento,
  atualizaTipoDocumento, deletaDepartamento,
  deletaTipoDocumento,
  listaTiposDocumento
} from './configs/functions';
import { DocumentTypeForm } from './DocumentTypeForm';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { useAppContext } from '../../../../contexts/AppContext';
import {useSearchParams} from "react-router-dom";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsTableFilter} from "./components/ItemsTableFilter";
import {ItemsNoCodeTableFilter} from "./components/ItemsNoCodeTableFilter";

const DocumentTypeRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
  const [openDialog, setOpenDialog] = useState(false);

  const deleteTipoDocumento = () => {
    const id = data.id;
    deletaTipoDocumento(id)
        .then((response) => {
          const message = response.data.message ?? 'Tipo de documento excluído com sucesso'
          handleExibirAlerta('Sucesso', message, 'success');
        }).catch((error) => {
      const message = error.response.data.message ?? 'Erro ao executar operação'
      handleExibirAlerta('Erro', message, 'error');
    }).finally(() => {
      if (updateData) updateData();
    })

    setOpenDialog(false)
  }

  return (
      <>
        <TableRow key={data.id}>
          <TableCell>{data.titulo}</TableCell>
          <TableCell>{data.observacao}</TableCell>
          <TableCell align='right' sx={{ minWidth: 110 }}>
            {hasPermission(["admin", "admin_tipoDocumento", "update_tipoDocumento"]) && (
              <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
                <EditIcon />
              </IconButton>
            )}
            {hasPermission(["admin", "admin_tipoDocumento", "delete_tipoDocumento"]) && (
              <IconButton aria-label="Excluir" onClick={() => setOpenDialog(true)}>
                <TrashIcon />
              </IconButton>
            )}
          </TableCell>
        </TableRow>

        <ConfirmDialog
            description={'Tem certeza que deseja excluir este Tipo de documento?'}
            title={`Excluir Departamento ${data.nome}`}
            goAction={deleteTipoDocumento}
            handleClose={setOpenDialog}
            state={openDialog}
        />
    </>
  );
};

export function ListOfDocumentType() {
  const { exibirAlerta } = useCommonItems();
  const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

  // estados para controle de paginacao
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [numTotalItems, setNumTotalItems] = useState(0);

  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [selectedTipoDocumento, setSelectedTipoDocumento] = useState(null);

  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamPage = searchParams.get('page');
  const searchParamPerPage = searchParams.get('perPage');

  const { data: empresasData, error: listaEmpresasError } = useQuery({
    queryKey: ['listaEmpresas'],
    queryFn: async () => {
      const response = await listaEmpresas(null, null, null, true);
      const empresasData = response.data.data;

      const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

      setEmpresaIdSession(selectedEmpresaId);

      setSearchParams((state) => {
        state.set('id_empresa', String(selectedEmpresaId));
        state.set('page', String(page + 1));
        state.set('perPage', String(rowsPerPage));

        return state;
      });
      return response;
    },
    refetchOnWindowFocus: false,
  });

  const queryFilters = {
    id_empresa: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
    titulo: searchParams.get('nome'),
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  const queryKeyListTipoDocumento = ['listOfTipoDocumento', queryFilters];
  const { data: tipoDocumentoData, isLoading, error: tipoDocumentoError, refetch } = useQuery({
    queryKey: queryKeyListTipoDocumento,
    queryFn: () => listaTiposDocumento(queryFilters),
    enabled: !!searchParamPage && !! searchParamPerPage,
  });

  function handleEditTipoDocumento(item) {
    setSelectedTipoDocumento(item);
    setOpenForm(true);
  }

  useEffect(() => {
    if (listaEmpresasError) {
      exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
    }

    if (tipoDocumentoError) {
      exibirAlerta('Erro', 'Erro ao carregar tipos de documento', 'error');
    }
  }, [exibirAlerta, listaEmpresasError, tipoDocumentoError])

  useEffect(() => {
    if (tipoDocumentoData) {
      setTiposDocumento(tipoDocumentoData.data.data);
      setNumTotalItems(tipoDocumentoData.data.numero_total);
      setSelectedEmpresaId(searchParams.get('id_empresa'));
      setEmpresas(empresasData.data.data)
    }
  }, [tipoDocumentoData]);

  useEffect(() => {
    if (openForm) return;
    setSelectedTipoDocumento(null);

    const queryFilters = { ...Object.fromEntries(searchParams) }

    const tableFilters = { ...queryFilters };
    delete tableFilters.page;
    delete tableFilters.perPage;

    const activeFilters = Object.keys(tableFilters).length;

    setActiveFilters(activeFilters);
    setIsOpenFilters(activeFilters > 0);

  }, [openForm, searchParams]);

  useEffect(() => {
    const requiredPermissionsView = ['admin', 'admin_tipoDocumento', 'list_tipoDocumento'];
    checkPermissionsAndRedirect(requiredPermissionsView);
  }, []);

  return (
    <>
      <Helmet title="Tipos de Documentos" defer={false} />

      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={3}>
        <Grid item xs={12}>
          <Breadcrumbs>
            <LinkNavegacao to="/">
              <HomeIcon fontSize="small" />
            </LinkNavegacao>
            <Typography variant="span">Gerenciamento</Typography>
            <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
            <Typography variant="span">Tipos de Documentos</Typography>
          </Breadcrumbs>
          <PageTitle
            icon={<ProductDocumentsIcon fontSize="large" />}
            title="Tipos de Documentos"
            description="Administre os tipos de documentos das empresas."
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <FiltersButton
                isOpenFilters={isOpenFilters}
                setIsOpenFilters={setIsOpenFilters}
                activeFilters={activeFilters}
            />

            {hasPermission(['admin', 'admin_tipoDocumento', 'create_tipoDocumento']) && (
              <Grid item xs={12} md={4}>
                <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  <Button variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
                    Cadastrar Tipo de Documento
                  </Button>
                </Typography>
              </Grid>
            )}
          </Stack>
        </Grid>

        <ItemsNoCodeTableFilter
            isOpenFilters={isOpenFilters}
            isLoadingList={isLoading}
            empresas={empresasData?.data?.data || []}
            item={"Tipo de documento"}
        />

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="Tipo de Documento">
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Observação</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <SkeletonTableRow
                    numCells={3}
                    numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                  />
                ) : tiposDocumento.length > 0 ? (
                  tiposDocumento.map((item) => (
                    <DocumentTypeRow
                        key={item.id}
                        data={item}
                        handleExibirAlerta={exibirAlerta}
                        handleOpenEditForm={handleEditTipoDocumento}
                        updateData={refetch}
                    />
                  ))
                ) : (
                  <EmptyTableRow colSpan={3} />
                )}
              </TableBody>
            </Table>
            <CustomTablePagination
              numTotalItems={numTotalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <DocumentTypeForm
        open={openForm}
        setOpen={(value) => {
          setOpenForm(value);
          if (!value) {
            setSelectedTipoDocumento(null);
          }
        }}
        documentType={selectedTipoDocumento}
        empresas={empresas}
        queryKey={queryKeyListTipoDocumento}
      />
    </>
  );
}